import { Markdown } from "@client/components/Markdown";
import { useT } from "@client/i18n";
import type { PrepLecture } from "@content/prepSchema";
import { AnchorLine } from "./AnchorLine";
import { BookBox } from "./BookBox";
import { MemoDetails } from "./MemoDetails";
import { Panel } from "./Panel";
import { ShowInModelButton } from "./ShowInModel";

type Interpretations = NonNullable<PrepLecture["interpretations"]>;

export function InterpretationsPanel({
  data,
  openPanels,
  onTogglePanel,
  onShow,
  className,
}: {
  data: Interpretations;
  openPanels: ReadonlySet<string>;
  onTogglePanel(id: string): void;
  onShow(presetId: string): void;
  className?: string;
}) {
  const t = useT();
  return (
    <Panel title={t("teacher.prep.panel.interpretations")} {...(className ? { className } : {})}>
      <BookBox>{data.bookBox}</BookBox>
      <ul className="space-y-3">
        {data.items.map((item) => (
          <li
            key={item.id}
            className="space-y-2 rounded-lg border border-ink-200 p-3 dark:border-ink-700"
          >
            <h3 className="font-medium text-ink-900 dark:text-ink-50">{item.title}</h3>
            <Markdown variant="compact">{item.question}</Markdown>
            <Markdown variant="compact">{item.body}</Markdown>
            <Markdown variant="compact">{item.explainsDifferently}</Markdown>
            <ShowInModelButton preset={item.preset} onShow={onShow} />
            <AnchorLine book={item.book} beyondBook={item.beyondBook} />
          </li>
        ))}
      </ul>
      <MemoDetails
        id="misreadings"
        summary={t("teacher.prep.misreadings")}
        open={openPanels}
        onToggle={onTogglePanel}
      >
        <ul className="space-y-3">
          {data.misreadings.map((misreading, index) => (
            <li key={index} className="space-y-2">
              <Markdown variant="compact">{misreading.claim}</Markdown>
              <Markdown variant="compact">{misreading.correction}</Markdown>
              <ShowInModelButton preset={misreading.preset} onShow={onShow} />
              <AnchorLine book={misreading.book} beyondBook={misreading.beyondBook} />
            </li>
          ))}
        </ul>
      </MemoDetails>
    </Panel>
  );
}
